<template>
  <div class="archived-seasons-section">
    <button
      v-if="collapsible && !forceExpanded"
      class="archived-seasons-section__title archived-seasons-section__title--toggle"
      type="button"
      :aria-expanded="isExpanded"
      @click.stop="isExpanded = !isExpanded"
    >
      {{ ARCHIVED_SECTION }}
      <span
        class="archived-seasons-section__caret"
        :class="{ 'archived-seasons-section__caret--expanded': isExpanded }"
        v-html="caretIcon"
      />
    </button>
    <div v-else class="archived-seasons-section__title">{{ ARCHIVED_SECTION }}</div>

    <slot v-if="isShown" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { ARCHIVED_SECTION } from '@/store/seasons';
import caretIcon from '@icons/other/caret-icon.svg';

const {
  collapsible = true,
  defaultExpanded = false,
  forceExpanded = false,
} = defineProps<{
  /** Without it the section is just a labelled group, always open. */
  collapsible?: boolean;
  defaultExpanded?: boolean;
  /** Opens a collapsed section without changing its toggle, e.g. while a search is typed. */
  forceExpanded?: boolean;
}>();

const isExpanded = defineModel<boolean>('expanded', { default: false });
if (defaultExpanded) isExpanded.value = true;
const isShown = computed(() => !collapsible || isExpanded.value || forceExpanded);
</script>

<style scoped lang="scss">
@import '@styles/variables';

.archived-seasons-section {
  width: 100%;

  &__title {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin-bottom: 0.8rem;
    padding: 0;
    font-size: 1.1rem;
    text-transform: uppercase;
    color: rgba($c-dark, 0.5);

    &--toggle {
      background: none;
      border: none;
      cursor: pointer;

      &:hover {
        color: rgba($c-dark, 0.8);
      }
    }
  }

  &__caret {
    width: 0.6rem;
    height: 0.6rem;
    display: inline-flex;
    fill: currentColor;
    transform: rotate(90deg);
    transition: transform 0.15s ease;

    :deep(svg) {
      width: 100%;
      height: 100%;
    }

    &--expanded {
      transform: rotate(-90deg);
    }
  }
}
</style>
